import { SegmentEffects } from '@opengov/app-core/analytics';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import {
  SHOW_USER,
  STATE_ALL,
  STATE_DRAFT,
  STATE_PUBLISHED,
  STATE_UNPUBLISHED,
  STORIES_ADMIN_PRIVILEGE,
  VIEW_LIST
} from '@opengov/stories-shared/constants';
import { IPublication } from '@opengov/stories-shared/types';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  IPublicationListMessages,
  IPublicationModalMessages,
  ISessionUser,
  ISortOptions,
  IStoriesFolder,
  IStoryListMessages,
  IStoryShowOptions,
  IStoryViewOptions,
  ISubjectClaims,
  ITabOptions
} from '../../../../types';
import {
  AUTHOR,
  PUBLICATIONS_NAME,
  STATUS,
  UPDATE_TIME
} from '../../../constants';
import { hasPrivilege, purgeUrlCache } from '../../../utils';
import { ControlBar } from '../../StoryList/ControlBar';
import { FolderSvg } from '../../StoryList/StoryListContent/FolderSvg';
import { CreatePublicationModal } from '../CreatePublicationModal/CreatePublicationModal';
import { PublicationDeleteModal } from '../PublicationDeleteModal';
import { styles } from './PublicationList.styles';

export type IPublicationListProps = {
  lastPath: string;
  rowData: IPublication[];
  isPublicationsLoading: boolean;
  storyMessages: IStoryListMessages;
  messages: IPublicationListMessages;
  publicationModalMessages: IPublicationModalMessages;
  isAdmin: boolean;
  entityId: number;
  entityUuid: string;
  entitySubdomain: string;
  entityName: string;
  folderStructure: IStoriesFolder[];
  user: ISessionUser;
  view: string;
  search: string;
  show: string;
  sort: string;
  tab: ITabOptions;
  publicationVisibility: string;
  publicationTitlesList: IPublication[];
  currentSubjectClaims: ISubjectClaims | null;
  isCustomSpreadsheetReportEnabled: boolean;
  updateSearch(search: string): void;
  updateShow(show: IStoryShowOptions): void;
  updatePublicationVisibility(publicationVisibility: string): void;
  updateSort(sort: ISortOptions): void;
  updateView(view: IStoryViewOptions): void;
  totalRows: number;
  page: number;
  rowsPerPage: number;
  getAllPublications?(): void;
  clearSearchFilters(): void;
  navigateToPublicationView(entitySubdomain: string, publicationId: string): any;
  onPageChange(newPage: number): void;
  onRowsPerPageChange(newRowsPerPage: number): void;
};

const chipColorByStatus: Record<string, 'success' | 'warning' | 'default'> = {
  [STATE_PUBLISHED]: 'success',
  [STATE_UNPUBLISHED]: 'warning',
};

export const renderStatusColumn = (statusProps: { value: string }): JSX.Element => (
  <Chip
    label={statusProps?.value ? statusProps.value.charAt(0).toUpperCase() + statusProps.value.slice(1) : ''}
    color={chipColorByStatus[statusProps?.value] ?? 'default'}
    size="small"
  />
);

export const calculateSegmentTrackData = (rowData: IPublication[]) => {
  return rowData.reduce(
    (data, publication) => {
      switch (publication.status) {
        case STATE_PUBLISHED:
          data.published_count++;
          const daysSincePublished = moment().diff(moment(publication.publishedAt), 'days');
          if (data.days_since_last_published === null || daysSincePublished < data.days_since_last_published) {
            data.days_since_last_published = daysSincePublished;
          }
          return data;
        case STATE_DRAFT:
          data.draft_count++;
          return data;
        case STATE_UNPUBLISHED:
          data.unpublished_count++;
          return data;
        default:
          return data;
      }
    },
    {
      draft_count: 0,
      unpublished_count: 0,
      published_count: 0,
      days_since_last_published: null as number | null
    }
  );
};

const useSegmentTrackerForPublicationData = (rowData: IPublication[]) => {
  const segmentTrackData = useMemo(() => calculateSegmentTrackData(rowData), [rowData]);
  const { draft_count, unpublished_count, published_count, days_since_last_published } = segmentTrackData;
  const hasData = rowData.length > 0;

  useEffect(() => {
    if (hasData) {
      void SegmentEffects.track({
        eventName: 'Stories: Publications Status',
        props: segmentTrackData
      });
    }
  }, [hasData, draft_count, unpublished_count, published_count, days_since_last_published]);
};

export const PublicationCardGrid: React.FC<{
  publications: IPublication[];
  messages: IPublicationListMessages;
  maxHeight: string;
  onClick(publication: IPublication): void;
}> = ({ publications, messages, maxHeight, onClick }) => (
  <Box sx={{ ...styles.cardGrid, maxHeight }}>
    {publications.map((publication) => {
      const formattedAuthor = `${publication.createdByUser.firstName} ${publication.createdByUser.lastName}`;
      const metadata = `${messages.createdBy} ${formattedAuthor} ${moment.utc(publication.createdAt).fromNow()}`;
      return (
        <Box key={publication.id} sx={styles.card} onClick={() => onClick(publication)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(publication); } }} aria-label={publication.title}>
          {publication.thumbnailPath ? (
            <Box component="img" src={purgeUrlCache(publication.thumbnailPath)} alt={publication.title} sx={styles.cardImage} />
          ) : (
            <Box sx={styles.cardImagePlaceholder}>
              <ImageOutlined fontSize="large" aria-hidden="true" />
            </Box>
          )}
          <Box sx={styles.cardContent}>
            <Typography variant="body2" fontWeight={600}>{publication.title}</Typography>
            <Typography variant="caption" color="text.secondary">{metadata}</Typography>
          </Box>
        </Box>
      );
    })}
  </Box>
);

export const PublicationList = (props: IPublicationListProps) => {
  const {
    lastPath,
    storyMessages,
    rowData,
    messages,
    publicationModalMessages,
    isAdmin,
    entityId,
    entityUuid,
    entitySubdomain,
    folderStructure,
    user,
    entityName,
    isPublicationsLoading,
    search,
    show,
    sort,
    tab,
    view,
    publicationVisibility,
    publicationTitlesList,
    currentSubjectClaims,
    isCustomSpreadsheetReportEnabled,
    updateSearch,
    updateShow,
    updateSort,
    updateView,
    clearSearchFilters,
    navigateToPublicationView,
    updatePublicationVisibility,
    totalRows,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
  } = props;

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedPublicationToDelete, setSelectedPublicationToDelete] = useState<IPublication | null>(null);
  const [isCreatePublicationModalVisible, setIsCreatePublicationModalVisible] = useState(false);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [tableMaxHeight, setTableMaxHeight] = useState<string>('calc(100vh - 400px)');

  useEffect(() => {
    const updateHeight = () => {
      if (tableWrapperRef.current) {
        const offsetTop = tableWrapperRef.current.getBoundingClientRect().top;
        const paginationHeight = totalRows > 0 ? 52 : 0;
        setTableMaxHeight(`calc(100vh - ${offsetTop + paginationHeight + 8}px)`);
      }
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    if (tableWrapperRef.current) observer.observe(document.body);
    window.addEventListener('resize', updateHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, [totalRows]);

  useSegmentTrackerForPublicationData(rowData);

  useEffect(() => {
    if (!isEmpty(selectedPublicationToDelete)) {
      setIsDeleteModalVisible(true);
    } else {
      setIsDeleteModalVisible(false);
    }
  }, [selectedPublicationToDelete]);

  const canDelete = currentSubjectClaims && hasPrivilege(currentSubjectClaims, STORIES_ADMIN_PRIVILEGE);

  const handleRowClick = (publication: IPublication) => {
    navigateToPublicationView(entitySubdomain, publication.id);
  };

  const handleDeleteClick = (e: React.MouseEvent, publication: IPublication) => {
    e.stopPropagation();
    setSelectedPublicationToDelete(publication);
  };

  const handelModalClose = () => {
    setIsDeleteModalVisible(false);
    setSelectedPublicationToDelete(null);
  };

  if (
    rowData.length <= 0 &&
    !isPublicationsLoading &&
    search.length <= 0 &&
    show !== SHOW_USER &&
    publicationVisibility === STATE_ALL
  ) {
    return (
      <Box sx={styles.centered}>
        <FolderSvg />
        <Typography variant="h5" data-test="publications-welcome-heading-folder">
          {messages.welcomeToPublications}
        </Typography>
        <Typography variant="body1">
          {isAdmin ? messages.youHaveNoPublications : messages.youHaveNoPublicationsNonAdmin}
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            size="small"
            onClick={() => setIsCreatePublicationModalVisible(true)}
            data-test="create-publication-button"
          >
            {messages.createNewPublication}
          </Button>
        )}
        {isCreatePublicationModalVisible &&
          createPortal(
            <CreatePublicationModal
              isAdmin={isAdmin}
              messages={publicationModalMessages}
              folderStructure={folderStructure}
              onClose={() => setIsCreatePublicationModalVisible(false)}
              user={user}
              entitySubdomain={entitySubdomain}
              entityId={entityId}
              entityName={entityName}
              publicationList={publicationTitlesList}
            />,
            document.getElementById('app') ?? document.body
          )}
      </Box>
    );
  }

  const renderContent = () => {
    if (rowData.length <= 0 && !isPublicationsLoading && (search.length > 0 || show === SHOW_USER)) {
      return (
        <Box sx={styles.centered}>
          <Typography variant="h5" data-test="publications-none-found-heading">
            {messages.noPublicationsFound}
          </Typography>
          <Typography variant="body1">{storyMessages.adjustFilters}</Typography>
          <Button variant="outlined" size="small" onClick={clearSearchFilters} data-test="clear-filters-button">
            {storyMessages.clearFilters}
          </Button>
        </Box>
      );
    }

    if (isPublicationsLoading) {
      return (
        <Box sx={styles.spinner} data-test="get-publications-message">
          <CircularProgress data-test="loading-spinner" />
        </Box>
      );
    }

    return (
      <Box ref={tableWrapperRef} sx={styles.container} data-test="og-grid-table-container">
        {isDeleteModalVisible &&
          selectedPublicationToDelete &&
          createPortal(
            <PublicationDeleteModal
              isDeleteSection={false}
              messages={messages}
              selectedPublicationId={selectedPublicationToDelete.id}
              publicationName={selectedPublicationToDelete.title}
              publicationUrl={selectedPublicationToDelete.publicUrl}
              onClose={handelModalClose}
              entityId={entityId}
              entityUuid={entityUuid}
              entitySubdomain={entitySubdomain}
              userId={user.id}
              isAdmin={isAdmin}
              isPublicationEmpty={selectedPublicationToDelete?.foldersCount?.aggregate?.count === 1}
              isCustomSpreadsheetReportEnabled={isCustomSpreadsheetReportEnabled}
            />,
            document.getElementById('app') ?? document.body
          )}

        {view !== VIEW_LIST ? (
          <PublicationCardGrid publications={rowData} messages={messages} maxHeight={tableMaxHeight} onClick={handleRowClick} />
        ) : (
          <Box sx={{ ...styles.tableWrapper, maxHeight: tableMaxHeight }}>
            <Table stickyHeader>
              <TableHead sx={styles.tableHead}>
                <TableRow>
                  <TableCell scope="col">{PUBLICATIONS_NAME}</TableCell>
                  <TableCell scope="col">{STATUS}</TableCell>
                  <TableCell scope="col">{AUTHOR}</TableCell>
                  <TableCell scope="col">{UPDATE_TIME}</TableCell>
                  {canDelete && <TableCell scope="col">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rowData.map((publication) => {
                  const userData = publication.createdByUser;
                  const userName = `${userData?.firstName} ${userData?.lastName}`;
                  const initials = userName.toUpperCase().split(' ').reduce((acc, part) => acc + (part[0] ?? ''), '');

                  return (
                    <TableRow
                      key={publication.id}
                      sx={styles.tableRow}
                      onClick={() => handleRowClick(publication)}
                      tabIndex={0}
                      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); handleRowClick(publication); } }}
                      aria-label={`Open ${publication.title}`}
                    >
                      <TableCell>
                        <Box sx={styles.publicationNameWrapper}>
                          {publication.thumbnailPath ? (
                            <Box
                              component="img"
                              src={purgeUrlCache(publication.thumbnailPath)}
                              alt={publication.title}
                              sx={styles.thumbnail}
                            />
                          ) : (
                            <Box sx={styles.thumbnailPlaceholder}>
                              <ImageOutlined aria-hidden="true" />
                            </Box>
                          )}
                          <Typography variant="body2" fontWeight={600}>
                            {publication.title}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {renderStatusColumn({ value: publication.status })}
                      </TableCell>
                      <TableCell>
                        <Box sx={styles.createdByWrapper}>
                          <Box sx={styles.avatar}>{initials}</Box>
                          <Box>
                            <Typography variant="body2">{userName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {moment.utc(publication.createdAt).fromNow()}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell data-test="updated-at">
                        <Typography variant="body2">
                          {moment(publication.updatedAt).format('MM/DD/YYYY [at] h:mm a')}
                        </Typography>
                      </TableCell>
                      {canDelete && (
                        <TableCell align="left" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title={`Delete ${publication.title}`}>
                            <IconButton
                              color="error"
                              size="small"
                              data-test="publication-delete-icon"
                              aria-label={`Delete ${publication.title}`}
                              onClick={(e) => handleDeleteClick(e, publication)}
                            >
                              <DeleteOutlined fontSize="small" aria-hidden="true" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
        {totalRows > 0 && (
          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[20, 40, 60]}
            onPageChange={(_, newPage) => onPageChange(newPage)}
            onRowsPerPageChange={(e) => onRowsPerPageChange(Number(e.target.value))}
            data-test="publications-pagination"
            sx={styles.pagination}
            SelectProps={{ inputProps: { 'aria-label': 'Rows per page' } }}
          />
        )}
      </Box>
    );
  };

  return (
    <Box data-test="publications-view">
      <ControlBar
        lastPath={lastPath}
        messages={storyMessages}
        publicationMessages={messages}
        search={search}
        show={show}
        sort={sort}
        tab={tab}
        view={view}
        publicationVisibility={publicationVisibility}
        updateSearch={updateSearch}
        updateShow={updateShow}
        updateSort={updateSort}
        updateView={updateView}
        updatePublicationVisibility={updatePublicationVisibility}
      />
      {renderContent()}
    </Box>
  );
};
